// biome-ignore-all lint/a11y/useSemanticElements: WAI-ARIA grid pattern
//   uses role=grid + role=row + role=gridcell on generic <div> containers
//   with <button> descendants for the keyboard FSM. <table>/<tr>/<td>
//   semantics do not apply — this is interactive layout, not tabular data.
//   Per 14-RESEARCH §"Pattern 2: WAI-ARIA grid with roving tabindex".
// biome-ignore-all lint/a11y/useFocusableInteractive: in the ARIA grid
//   pattern, role=row + role=gridcell ARE interactive but the focusable
//   target is the <button> descendant (single tabbable per roving tabindex
//   invariant). Focus does not live on the row/gridcell wrappers.
// biome-ignore-all lint/a11y/useAriaPropsSupportedByRole: aria-selected on
//   a <button> inside role=gridcell is supported per ARIA 1.2 (gridcell's
//   selection state is announced via aria-selected; the focusable button
//   inherits the cell's selected affordance via this attribute).
//
// Plan 14-06 — MatchGrid (MUI-07 parallel-DOM accessible grid).
//
// The parallel-DOM accessibility surface mounted alongside the PixiJS canvas
// (Plan 14-03 wired the canvas wrapper's pointerEvents:'none', so DOM events
// fall through to the overlays AND to this grid). Exposes a WAI-ARIA grid
// pattern (role=grid + role=row + role=gridcell) with roving tabindex +
// arrow-key navigation. The Enter key routes the focused cell's intent into
// the same $turnBuilder atom that WordTraceLayer + PieceDragLayer write to —
// so a keyboard user can drive Phase A trace + Phase B move/empower + the
// phase-switch dispatch without any pointer interaction.
//
// Source: WAI-ARIA APG "Layout Grid" pattern + 14-RESEARCH.md §"Pattern 2:
// WAI-ARIA grid with roving tabindex". This is the FIRST WAI-ARIA grid
// surface in the codebase; Phase 18's WCAG 2.2 A/AA pass refines the
// structural foundation this plan lays down.
//
// data-cell-idx is the SHARED hit-target attribute. The three production
// surfaces that read it via `document.elementFromPoint(x,y).closest(
// '[data-cell-idx]')` are WordTraceLayer (Plan 14-02), PieceDragLayer
// (Plan 14-03), and the cell-pick follow-ups in Plan 14-04/05/06. Keep the
// attribute byte-equal across all of them.
//
// Keyboard model (locked in 14-CONTEXT.md):
//   ArrowUp/Down/Left/Right — clamp at edges (no wrap)
//   Enter:
//     Phase A=word        → append-or-init / restart / reuse-noop trace
//     Phase B=move pre    → set source (only if focused cell has your piece)
//     Phase B=move post   → set destination
//     Empower (armed)     → if own non-king piece, commit the empower target
//                            via onEmpowerPieceTap (role-first flow; the role
//                            was already chosen in EmpowerModal)
//   Escape:
//     Phase A=word        → clear the partial trace
//     Phase B=move post   → reset source (back to pre-source state)
//   F:
//     Always              → dispatch CustomEvent(
//                            'runegambit:open-phase-switch-menu')
//
// DEC-021 (server authority): the client-side adjacency / own-piece /
// blank-piece checks are ergonomic only. A tampered keystroke produces the
// same payload shape as any other input; the backend's validateWord +
// validateMove + validateEmpower remain the legality authorities.
//
// Pieces (cell.piece_kind):
//   'blank' | 'king' | null  — the board carries piece_kind directly; there
//   is NO nested `cell.piece.role` shape (Plan 14-03 SUMMARY auto-fix #3
//   documents the corrected projection). Empower's target is therefore
//   "own BLANK piece" (piece_kind === 'blank' && piece_owner_slot ===
//   your_slot); the king is non-empowerable per DEC-023.

import { useStore } from '@nanostores/react'
import { type JSX, useCallback, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { isEligibleAnchorCell } from '../../game/anchor-eligibility'
import type { BoardView } from '../../game/board-view'
import {
  boardRows,
  CHESS_CELLS,
  cellIdxToScreenRow,
  screenToCellIdx,
} from '../../game/layers/board-layout'
import type { PlayerView, RoleKind } from '../../game/PlayerViewSchema'
import { resolveDragSource } from '../../game/staged-move'
import { $turnBuilder, setPhaseA, stageMove } from '../../stores/turnBuilder'
import { useAnnounce } from '../components/useAnnounce'

export interface MatchGridProps {
  readonly playerView: PlayerView
  /**
   * Active board view. The word-trace cell highlight (`data-cell-in-trace`)
   * is driven by the persisted Phase A path, which lives in $turnBuilder for
   * the whole turn (until TURN_APPLIED). It must only paint in the Letters
   * view — otherwise a submitted word's yellow highlight bleeds through the
   * transparent grid into the Chess view. Defaults to 'letters' so existing
   * tests that mount MatchGrid in isolation keep their trace-highlight
   * assertions.
   */
  readonly boardView?: BoardView
  /**
   * Server-authored legal destinations per own-piece cell_idx
   * (PlayerView.your_legal_moves). Used by the chess-view keyboard move FSM
   * to gate drops + paint the `data-legal-target` highlight. Defaults to `{}`
   * so existing letters-view tests need not pass it.
   */
  readonly legalMoves?: Readonly<Record<number, ReadonlyArray<number>>>
  /**
   * The optimistic staged move (from $turnBuilder phaseB, sentinel-filtered).
   * Lets the keyboard FSM re-pick the original source when the focused cell is
   * the staged target (mirrors PieceDragLayer's pointer re-pick). `null` when
   * no real move is staged.
   */
  readonly stagedMove?: { from: number; to: number } | null
  /**
   * Empower clarity (role-first flow): true while the board is ARMED for
   * empower piece-selection (the player chose a role in EmpowerModal and must
   * now tap which piece to empower). When set, the caller's empowerable pieces
   * (own non-king pieces) glow via `data-empower-target` and a tap on one routes
   * to `onEmpowerPieceTap` instead of the word/move handlers.
   */
  readonly empowerArming?: boolean
  /** Called with the tapped cell_idx when an eligible piece is chosen while arming. */
  readonly onEmpowerPieceTap?: (cellIdx: number) => void
  /**
   * ARCA-591 — keyboard re-empower hook. Called with a cell_idx when the player
   * re-presses Enter on an already-picked-up OWN empowered piece (the keyboard
   * analog of the PieceDragLayer same-cell tap). The consumer opens the
   * ExtendModal; this layer makes no eligibility decision beyond "own empowered
   * non-king piece" (server stays authoritative — DEC-021). Optional.
   */
  readonly onInspectPieceTap?: (cellIdx: number) => void
}

/**
 * A cell is a legitimate empower target iff it carries the caller's own
 * non-king piece — exactly the backend `validateEmpower` predicate
 * (apps/backend/src/match/validation/chess.ts: piece_owner_slot === slot &&
 * piece_kind !== null && piece_kind !== 'king', i.e. a 'blank' piece). The
 * client gate is ergonomic only (DEC-021 — server stays authoritative).
 */
function isEmpowerEligibleCell(
  cell: PlayerView['board'][number] | undefined,
  yourSlot: 0 | 1,
): boolean {
  return (
    cell !== undefined &&
    cell.piece_owner_slot === yourSlot &&
    cell.piece_kind === 'blank'
  )
}

// COLS is fixed; ROWS/TOTAL are derived at render from playerView.board.length
// because the Runespring field GROWS mid-match (36 → 48 → 60 cells, i.e.
// 6 → 8 → 10 rows) via the T6 flop + T11 river reveals. See board-layout.ts
// `boardRows`. The flip below must use the live row count so it stays pixel-
// aligned with the Pixi canvas and so already-placed cells don't re-flip.
const COLS = 6

function rowOf(i: number): number {
  return Math.floor(i / COLS)
}
function colOf(i: number): number {
  return i % COLS
}

// The screen↔cell_idx flip (screenToCellIdx / cellIdxToScreenRow) is the
// SHARED helper from board-layout.ts — single source of truth so the DOM grid
// and the Pixi canvas never paint the same cell at mirrored rows.

/**
 * Visually-hidden style. Inline so the grid does not depend on a global
 * `.sr-only` CSS class (none exists in this codebase yet — the Phase 18
 * a11y polish will likely add one as a design-token-driven utility). The
 * shape is the canonical visually-hidden recipe from MDN / WAI-ARIA APG.
 */
const SR_ONLY: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
}

/**
 * Build the { cell_idx → letter } lookup the cell aria-labels use.
 *
 * Plan 30-04 refresh: Runespring unifies the field — every cell with a
 * non-null `letter` participates in word tracing (Hollow cells return
 * letter===null and are skipped, breaking adjacency per UI-SPEC §2 z=7).
 */
function buildLetterMap(playerView: PlayerView): Map<number, string> {
  const map = new Map<number, string>()
  for (const cell of playerView.board) {
    if (cell.letter !== null && !cell.is_hollow) {
      map.set(cell.cell_idx, cell.letter)
    }
  }
  return map
}

export function MatchGrid({
  playerView,
  boardView = 'letters',
  legalMoves = {},
  stagedMove = null,
  empowerArming = false,
  onEmpowerPieceTap,
  onInspectPieceTap,
}: MatchGridProps): JSX.Element {
  const intl = useIntl()
  const turn = useStore($turnBuilder)
  const announce = useAnnounce()
  // ARCA-749 (WS3) — the grid spans the ACTIVE view's grid. The chess view is
  // the FIXED 6×6 (36 cells / 6 rows) and never grows on a Flop/River reveal;
  // the letters view tracks the growing word field (board.length: 36 → 48 → 60).
  // Cell DATA is read from the combined `board` below (it overlays both surfaces,
  // and in chess view i ∈ 0..35 so the piece columns are present) — only the
  // rendered SIZE is capped per view. Re-derived each render so a WS-driven
  // growth immediately renders the new word rows + their <button data-cell-idx>.
  const TOTAL = boardView === 'chess' ? CHESS_CELLS : playerView.board.length
  const ROWS = boardRows(TOTAL)
  const [focusedCell, setFocusedCell] = useState(0)
  // Chess-view keyboard move FSM: the cell_idx of the piece currently "in
  // hand" (picked up via Enter), or null when no piece is held. The Enter
  // handler branches on this to either pick up (null → idx) or drop (idx →
  // staged move). Cleared on Escape or after a successful drop.
  const [pickedUp, setPickedUp] = useState<number | null>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  // WR-05 fix (Phase 14 fix-pass iter 1): memoize the letterByCell map so
  // `applyCellSelection` and the `onKeyDown` handler that depends on it
  // are not reconstructed on every render. The pre-fix code rebuilt the
  // Map inline, so even though the useCallback dep array listed
  // `letterByCell`, the new Map reference on each render forced
  // reconstruction — negating memoization. Worse, onKeyDown's prop
  // identity churn meant React detached/reattached the grid keyboard
  // handler on every render, which can drop keystrokes during a
  // mid-render. With useMemo keyed on playerView, both callbacks
  // stabilize until playerView itself changes (i.e. on TURN_APPLIED).
  const letterByCell = useMemo(() => buildLetterMap(playerView), [playerView])

  /**
   * Programmatically focus the grid cell at index `idx` AND update the
   * roving-tabindex state. We render with `tabIndex={focusedCell===i?0:-1}`
   * so the state update triggers a re-render that moves the tabbable
   * element; we then chain a `.focus()` so keyboard focus follows.
   */
  const focusCell = useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= TOTAL) return
      setFocusedCell(idx)
      // We have to focus AFTER the new tabIndex lands on the target. A
      // microtask is sufficient for React 19's auto-batched updates; the
      // querySelector walks the current DOM and the focused element ends
      // up with both tabIndex=0 AND document.activeElement.
      queueMicrotask(() => {
        const el = gridRef.current?.querySelector<HTMLButtonElement>(
          `[data-cell-idx="${idx}"]`,
        )
        el?.focus()
      })
    },
    [TOTAL],
  )

  /**
   * Apply the focused cell's intent based on the current $turnBuilder
   * state. Read live via `.get()` so the handler always sees the freshest
   * value (the closure was created at render-time; React 19's batched
   * state updates can leave a stale `turn` snapshot).
   */
  const applyCellSelection = useCallback(
    (i: number) => {
      // Empower clarity (role-first flow): while the board is armed for empower
      // piece-selection, a tap on the caller's own non-king piece commits the
      // target (MatchScreen owns the setPhaseB + bloom + QueenRises). Taps on
      // ineligible cells are inert. This supersedes the word/move handlers.
      if (empowerArming) {
        if (isEmpowerEligibleCell(playerView.board[i], playerView.your_slot)) {
          onEmpowerPieceTap?.(i)
        }
        return
      }
      // T2/T3 anchor placement — server set `requires_anchor_placement`.
      // The Pixi AnchorPlacementOverlay paints the eligible cells but sits
      // BELOW this DOM grid in the stacking order, so cell taps land on
      // MatchGrid's <button> before they can reach the Pixi overlay. Route
      // them here. Eligibility shares the overlay's isEligibleAnchorCell
      // predicate so the visible targets and the tappable cells stay in lock-
      // step — in particular piece-occupied cells (e.g. the opponent king) are
      // NOT dispatchable, sparing the backend `cell_has_piece` 409 (ARCA-599).
      // The placeAnchor POST + failure handling live in MatchScreen, reached
      // via the same window CustomEvent pattern as the empower-sheet handoff.
      if (playerView.requires_anchor_placement === true) {
        const cell = playerView.board[i]
        if (cell && isEligibleAnchorCell(cell)) {
          window.dispatchEvent(
            new CustomEvent('runegambit:place-anchor', {
              detail: { cell_idx: i },
            }),
          )
        }
        return
      }
      const cur = $turnBuilder.get()
      // Phase A — word trace.
      if (cur.phaseA?.kind === 'word') {
        const path = cur.phaseA.path
        const last = path.length === 0 ? null : path[path.length - 1]
        const letter = letterByCell.get(i) ?? ''
        if (last === null || last === undefined) {
          // Empty path → seed with focused cell.
          setPhaseA({ kind: 'word', word: letter, path: [i] })
          return
        }
        if (path.includes(i)) {
          // Reuse — no-op (the keyboard analog of pointer-reuse abort).
          return
        }
        const dr = Math.abs(rowOf(i) - rowOf(last))
        const dc = Math.abs(colOf(i) - colOf(last))
        const isAdjacent = dr <= 1 && dc <= 1 && dr + dc > 0
        if (isAdjacent) {
          const newPath = [...path, i]
          const word = newPath.map((j) => letterByCell.get(j) ?? '').join('')
          setPhaseA({ kind: 'word', word, path: newPath })
        } else {
          // Non-adjacent jump → restart the path with the new cell.
          setPhaseA({ kind: 'word', word: letter, path: [i] })
        }
        return
      }
      // ARCA-619 — Phase B (chess) is the COMPOSITE step: a move is staged via
      // the pointer drag (PieceDragLayer) or the keyboard pickup/drop FSM
      // (applyChessSelection below, gated on boardView==='chess'), and empower
      // via the role-first arming branch at the top of this handler. There is no
      // segment-driven move FSM on this onClick (word-view) path any more.
    },
    [playerView, letterByCell, empowerArming, onEmpowerPieceTap],
  )

  /**
   * Chess-view Enter handler — the keyboard analog of PieceDragLayer's
   * pickup/drop pointer flow. Mirrors the `data-legal-target` highlight so a
   * single CSS recipe covers pointer + keyboard. All legality is server-
   * authored via `legalMoves` (DEC-021): the client gate is ergonomic only.
   */
  const applyChessSelection = useCallback(
    (i: number) => {
      // Empower clarity (role-first flow): keyboard analog of the pointer tap —
      // Enter on a focused eligible piece commits the empower target while armed.
      if (empowerArming) {
        if (isEmpowerEligibleCell(playerView.board[i], playerView.your_slot)) {
          onEmpowerPieceTap?.(i)
        }
        return
      }
      if (pickedUp === null) {
        const cell = playerView.board[i]
        const isOwnPiece =
          cell?.piece_owner_slot === playerView.your_slot &&
          cell?.piece_kind !== null
        const isStagedTarget = stagedMove !== null && i === stagedMove.to
        if (!isOwnPiece && !isStagedTarget) return
        const source = resolveDragSource(i, stagedMove)
        setPickedUp(source)
        const count = (legalMoves[source] ?? []).length
        // ARCA-591 — when the picked-up piece is an own empowered piece, hint
        // the keyboard re-empower gesture (re-press Enter on the same cell).
        const srcCell = playerView.board[source]
        const canReEmpower =
          srcCell?.piece_owner_slot === playerView.your_slot &&
          srcCell?.role_kind != null &&
          srcCell.role_kind !== 'king'
        announce(
          `Picked up piece. ${count} legal ${count === 1 ? 'move' : 'moves'}.${
            canReEmpower ? ' Press Enter again to re-empower.' : ''
          }`,
          'assertive',
        )
        return
      }
      // ARCA-591 — re-press Enter on the picked-up OWN empowered piece →
      // re-empower (the keyboard analog of the PieceDragLayer same-cell tap).
      // Only fires for own empowered non-king pieces; a blank/king piece falls
      // through to the existing "Illegal move" no-op so move pickup is intact.
      if (i === pickedUp) {
        const cell = playerView.board[i]
        if (
          cell?.piece_owner_slot === playerView.your_slot &&
          cell.role_kind != null &&
          cell.role_kind !== 'king'
        ) {
          setPickedUp(null)
          onInspectPieceTap?.(i)
          return
        }
      }
      // A piece is in hand — attempt to drop it on the focused cell.
      if ((legalMoves[pickedUp] ?? []).includes(i)) {
        stageMove({ from: pickedUp, to: i })
        setPickedUp(null)
        announce(`Move staged to position ${i + 1}.`, 'assertive')
      } else {
        announce('Illegal move.', 'assertive')
      }
    },
    [
      pickedUp,
      playerView.board,
      playerView.your_slot,
      legalMoves,
      stagedMove,
      announce,
      empowerArming,
      onEmpowerPieceTap,
      onInspectPieceTap,
    ],
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Arrow keys operate on SCREEN coordinates so ArrowDown always moves
      // visually downward regardless of slot. cellIdxToScreenRow applies the
      // same flip the canvas uses; screenToCellIdx maps back to cell_idx.
      const yourSlot = playerView.your_slot
      const screenRow = cellIdxToScreenRow(focusedCell, yourSlot, ROWS)
      const col = colOf(focusedCell)
      let next = focusedCell
      if (e.key === 'ArrowRight') {
        next =
          col < COLS - 1
            ? screenToCellIdx(screenRow, col + 1, yourSlot, ROWS)
            : focusedCell
      } else if (e.key === 'ArrowLeft') {
        next =
          col > 0
            ? screenToCellIdx(screenRow, col - 1, yourSlot, ROWS)
            : focusedCell
      } else if (e.key === 'ArrowDown') {
        next =
          screenRow < ROWS - 1
            ? screenToCellIdx(screenRow + 1, col, yourSlot, ROWS)
            : focusedCell
      } else if (e.key === 'ArrowUp') {
        next =
          screenRow > 0
            ? screenToCellIdx(screenRow - 1, col, yourSlot, ROWS)
            : focusedCell
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (boardView === 'chess') {
          applyChessSelection(focusedCell)
        } else {
          applyCellSelection(focusedCell)
        }
        return
      } else if (e.key === 'Escape') {
        e.preventDefault()
        if (boardView === 'chess') {
          if (pickedUp !== null) {
            setPickedUp(null)
            announce('Pickup cancelled.', 'assertive')
          } else if (stagedMove !== null) {
            // ARCA-619 — a real staged move exists; clear the move slot.
            stageMove(null)
            announce('Staged move cleared.', 'assertive')
          }
          return
        }
        const cur = $turnBuilder.get()
        if (cur.phaseA?.kind === 'word') {
          setPhaseA({ kind: 'word', word: '', path: [] })
        }
        return
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault()
        window.dispatchEvent(
          new CustomEvent('runegambit:open-phase-switch-menu'),
        )
        return
      }
      if (next !== focusedCell) {
        e.preventDefault()
        focusCell(next)
      }
    },
    [
      focusedCell,
      applyCellSelection,
      applyChessSelection,
      focusCell,
      playerView.your_slot,
      boardView,
      pickedUp,
      stagedMove,
      announce,
      ROWS,
    ],
  )

  // Localized role labels — the Empower sheet (Plan 14-03) already ships
  // these 5 keys with sv parity, so the grid's aria-label narration reads
  // them out for screen readers in the user's language.
  function pieceRoleLabel(role: RoleKind): string {
    return intl.formatMessage({ id: `match.empower.role.${role}` })
  }

  function ownerLabel(ownerSlot: 0 | 1): string {
    return ownerSlot === playerView.your_slot
      ? intl.formatMessage({ id: 'match.grid.piece_owner.you' })
      : intl.formatMessage({ id: 'match.grid.piece_owner.opponent' })
  }

  // Only paint the trace highlight in the Letters view. The Phase A path
  // persists in $turnBuilder across the whole turn (it IS the chosen action),
  // so without this gate a submitted word's yellow cells bleed through into
  // the Chess view (the grid is transparent over the Pixi canvas).
  const inTracePath: ReadonlyArray<number> =
    boardView === 'letters' && turn.phaseA?.kind === 'word'
      ? turn.phaseA.path
      : []

  // Chess view: while a piece is picked up, paint the legal destination cells
  // with `data-legal-target` (the SAME attribute PieceDragLayer's pointer
  // highlight uses — one CSS recipe covers both surfaces).
  const legalTargets: ReadonlyArray<number> =
    boardView === 'chess' && pickedUp !== null
      ? (legalMoves[pickedUp] ?? [])
      : []

  // Pre-compute stable row + cell keys so React's reconciliation uses
  // semantic identity (not the array index). Rows are derived from the live
  // board length; row keys are stable per backend position so the existing
  // rows keep identity when flop/river rows are appended.
  const rowIds = Array.from({ length: ROWS }, (_, r) => `r${r}`)

  return (
    <div
      ref={gridRef}
      role="grid"
      aria-label={intl.formatMessage({ id: 'match.grid.aria_label' })}
      aria-rowcount={ROWS}
      aria-colcount={COLS}
      onKeyDown={onKeyDown}
      data-screen-region="match-grid"
      style={{
        position: 'absolute',
        inset: 0,
        // The grid is the a11y surface; the PixiJS canvas is the visual
        // surface. Both render the same playerView.board. Cells are kept
        // transparent so the canvas shows through; the focused-cell
        // outline + in-trace highlight are the only visible affordances.
        // The grid container itself does NOT swallow pointer events —
        // cell buttons are individual click targets so mouse users who
        // tab into the grid can still click cells (the click handler
        // mirrors the Enter keystroke contract via applyCellSelection).
        pointerEvents: 'auto',
        display: 'grid',
        gridTemplateRows: `repeat(${ROWS}, 1fr)`,
      }}
    >
      <span style={SR_ONLY}>
        <FormattedMessage id="match.grid.sr_instructions" />
      </span>
      {rowIds.map((rowKey, r) => (
        <div
          key={rowKey}
          role="row"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${COLS}, 1fr)`,
          }}
        >
          {Array.from({ length: COLS }, (_, c) => {
            // DEC-023 view orientation: the DOM grid mirrors the canvas's
            // cellIdxToScreen flip so the same cell paints at the same
            // visual position in both surfaces (no more doubled letters at
            // mirrored rows). (r, c) is the DISPLAY row/col; cell_idx is
            // backend-ordered and looked up via screenToCellIdx.
            const i = screenToCellIdx(r, c, playerView.your_slot, ROWS)
            const cellKey = `c${i}`
            const cell = playerView.board[i]
            const letter = letterByCell.get(i) ?? ''
            const isFocused = focusedCell === i
            const inTrace = inTracePath.includes(i)
            const isLegalTarget = legalTargets.includes(i)
            // Empower clarity: glow the caller's empowerable pieces while the
            // board is armed for empower piece-selection.
            const isEmpowerTarget =
              empowerArming && isEmpowerEligibleCell(cell, playerView.your_slot)
            // ARCA-591 — turns of empowerment remaining on an empowered piece:
            // max(0, role_expires_turn − current_turn_idx). Role data is public
            // per DEC-023, so this shows on BOTH players' pieces. null on
            // un-empowered pieces and the King (both carry role_expires_turn
            // null) — the badge + aria suffix are gated on it being non-null.
            const empowerTurnsLeft =
              cell &&
              cell.role_kind !== null &&
              cell.role_kind !== 'king' &&
              cell.role_expires_turn !== null
                ? Math.max(
                    0,
                    cell.role_expires_turn - playerView.current_turn_idx,
                  )
                : null
            // Compose aria-label. Cells with a piece narrate
            // "<letter> at position N of 36, your knight" via the
            // .._with_piece key + the localized owner + role strings.
            let ariaLabel: string
            if (
              cell &&
              cell.piece_owner_slot !== null &&
              (cell.piece_kind !== null || cell.role_kind !== null)
            ) {
              // Choose role wording: prefer the active role_kind if present,
              // otherwise the king/blank piece_kind. king maps to its
              // localized role label; a plain blank reads as its piece_kind.
              const roleWord =
                cell.role_kind !== null
                  ? pieceRoleLabel(cell.role_kind)
                  : cell.piece_kind === 'king'
                    ? pieceRoleLabel('king')
                    : // A bare blank piece — name it via the localized
                      // 'king' label as a fallback would be wrong. The
                      // backend always sets role_kind for empowered pieces;
                      // a bare blank has piece_kind='blank' + role_kind=null
                      // and is read as just 'blank' (English fallback —
                      // Phase 18 may localize 'blank' itself).
                      'blank'
              ariaLabel = intl.formatMessage(
                { id: 'match.grid.cell.aria_label_with_piece' },
                {
                  letter,
                  position: i + 1,
                  total: TOTAL,
                  pieceOwner: ownerLabel(cell.piece_owner_slot),
                  pieceRole: roleWord,
                },
              )
            } else if (cell?.is_hollow) {
              // Hollow cells carry no letter; a bare aria_label would read
              // " at position N of M" (leading space). Narrate the hollow.
              ariaLabel = intl.formatMessage(
                { id: 'match.grid.cell.aria_label_hollow' },
                { position: i + 1, total: TOTAL },
              )
            } else if (!letter) {
              // Empty/unrevealed cell — same leading-space hazard; announce it
              // as an explicit empty cell rather than a stray space.
              ariaLabel = intl.formatMessage(
                { id: 'match.grid.cell.aria_label_empty' },
                { position: i + 1, total: TOTAL },
              )
            } else {
              ariaLabel = intl.formatMessage(
                { id: 'match.grid.cell.aria_label' },
                { letter, position: i + 1, total: TOTAL },
              )
            }
            // ARCA-591 — append the empowerment turns-left to the SR narration
            // for empowered pieces (the visual badge below is aria-hidden).
            if (empowerTurnsLeft !== null) {
              ariaLabel = `${ariaLabel}. ${intl.formatMessage(
                { id: 'match.grid.cell.empower_turns_left' },
                { count: empowerTurnsLeft },
              )}`
            }
            return (
              // position:relative anchors the ARCA-591 turns-left badge to the
              // cell box without disturbing the CSS-grid layout.
              <div
                key={cellKey}
                role="gridcell"
                style={{ position: 'relative' }}
              >
                <button
                  type="button"
                  className="match-grid-cell"
                  data-cell-idx={i}
                  data-cell-in-trace={inTrace ? 'true' : 'false'}
                  data-legal-target={isLegalTarget ? 'true' : undefined}
                  data-empower-target={isEmpowerTarget ? 'true' : undefined}
                  // ARCA-142 — Tongue-Tie frozen cell → icy CSS treatment in
                  // pieces.css `[data-cell-frozen="true"]`, painted over the
                  // transparent button. Driven off the wire flag each render
                  // (transient — the freeze decays), so nothing to clear.
                  data-cell-frozen={
                    cell?.is_frozen === true ? 'true' : undefined
                  }
                  tabIndex={isFocused ? 0 : -1}
                  aria-label={ariaLabel}
                  aria-selected={inTrace ? 'true' : undefined}
                  onClick={() => applyCellSelection(i)}
                  onFocus={() => setFocusedCell(i)}
                  style={{
                    width: '100%',
                    // height:100% so the button fills its CSS-grid cell on
                    // a square board (cells are 1fr × 1fr; minHeight alone
                    // would leave the focus border at 44px even when the
                    // cell is 80px tall). 44px minHeight is preserved as
                    // the floor for very small viewports per the iOS/
                    // Android tap-target guideline.
                    height: '100%',
                    minHeight: '44px',
                    // Transparent so the underlying PixiJS canvas remains
                    // the visual surface. The in-trace highlight + scale
                    // animation are driven by pieces.css `.match-grid-cell`
                    // + `[data-cell-in-trace="true"]`.
                    background: 'transparent',
                    color: 'var(--color-text-primary)',
                    // No always-on focus border — keyboard nav is a
                    // last-priority citizen. The keyboard-only focus ring is a
                    // `:focus-visible` outline in pieces.css, so mouse/touch
                    // play never shows it and it never competes with the Pixi
                    // ownership border or the selection background fill.
                    border: 'none',
                    borderRadius: '4px',
                    fontFamily: 'Fredoka, sans-serif',
                    fontWeight: 700,
                    fontSize: '20px',
                    cursor: 'pointer',
                    // Empower clarity: an inset Sun-Yellow ring + soft wash marks
                    // the caller's empowerable pieces while arming. boxShadow
                    // (not border) avoids any layout shift over the Pixi cell.
                    ...(isEmpowerTarget
                      ? {
                          boxShadow: 'inset 0 0 0 3px var(--color-cta-primary)',
                          background:
                            'color-mix(in srgb, var(--color-cta-primary) 18%, transparent)',
                        }
                      : {}),
                  }}
                >
                  {/* The cell is intentionally visually empty — the PixiJS
                    canvas is the single visual surface for cell content
                    (LetterLayer in Letters view, PieceLayer in Chess view).
                    Painting the letter here too would double-render against
                    the canvas (and at mismatched orientations before the
                    DEC-023 screen-flip fix). The aria-label above carries
                    the letter + piece narration for screen readers, so
                    keyboard / SR users still get the full semantic. */}
                </button>
                {/* ARCA-591 — turns-left badge on empowered pieces (chess view
                    only; pieces aren't shown in the Letters view). Both players'
                    pieces (role public per DEC-023). aria-hidden — the count is
                    already in the cell aria-label above. pointer-events:none so
                    it never intercepts move-drag / empower / extend taps. Solid
                    near-black pill + Sun-Yellow text keeps WCAG-AA contrast over
                    the magic aura, and the corner placement avoids occluding the
                    Pixi role glyph. */}
                {boardView === 'chess' && empowerTurnsLeft !== null ? (
                  <span
                    aria-hidden="true"
                    data-empower-turns-left={empowerTurnsLeft}
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      minWidth: '16px',
                      height: '16px',
                      padding: '0 4px',
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 'var(--radius-pill, 999px)',
                      background: 'var(--color-inkwell)',
                      color: 'var(--color-cta-primary)',
                      border: '1px solid var(--color-cta-primary)',
                      fontFamily:
                        'var(--font-family-display, Fredoka, sans-serif)',
                      fontWeight: 700,
                      fontSize: '11px',
                      lineHeight: 1,
                      pointerEvents: 'none',
                      zIndex: 5,
                    }}
                  >
                    {empowerTurnsLeft}
                  </span>
                ) : null}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
